import type { ReactNode } from 'react'
import { Loader2, WifiOff, AlertTriangle, SearchX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { isApiError } from '@/lib/problem'
import { Button } from './Button'

/** §14 — skeletons match the final layout; spinners live inside buttons only. */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('skeleton', className)} />
}

export function BookCardSkeleton() {
  return (
    <div className="card flex flex-col gap-2 p-3">
      <Skeleton className="aspect-[3/4] w-full rounded-sm" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  )
}

export function BookGridSkeleton({ count = 12 }: { count?: number }) {
  return (
    <div
      role="status"
      aria-busy="true"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
    >
      {Array.from({ length: count }, (_, i) => (
        <BookCardSkeleton key={i} />
      ))}
    </div>
  )
}

export function SubjectGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div role="status" aria-busy="true" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="card flex flex-col gap-2 p-4">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-12 w-24" />
        </div>
      ))}
    </div>
  )
}

export function DetailSkeleton() {
  return (
    <div role="status" aria-busy="true" className="grid gap-6 lg:grid-cols-3">
      <div className="flex flex-col gap-4 lg:col-span-2">
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-9 w-4/5" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
      <Skeleton className="h-80 w-full" />
    </div>
  )
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useTranslation()
  return (
    <span role="status" className="inline-flex items-center gap-2 text-text-muted">
      <Loader2 aria-hidden className="size-4 animate-spin" />
      <span className="text-meta">{label ?? t('state.loading')}</span>
    </span>
  )
}

export function EmptyState({
  icon,
  title,
  body,
  action,
  className,
}: {
  icon?: ReactNode
  title: string
  body?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-md border border-dashed border-border-strong bg-surface px-6 py-12 text-center',
        className,
      )}
    >
      <span aria-hidden className="text-text-muted">
        {icon ?? <SearchX className="size-8" strokeWidth={1.75} />}
      </span>
      <h3 className="text-section">{title}</h3>
      {body && <div className="max-w-reading text-body text-text-muted">{body}</div>}
      {action}
    </div>
  )
}

/**
 * §14 — a short human sentence, a retry, and the trace id in muted monospace so
 * a user can quote it. Transport failures are reported as offline, not as a
 * server error.
 */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown
  onRetry?: () => void
  className?: string
}) {
  const { t } = useTranslation('errors')
  const apiError = isApiError(error) ? error : null
  const offline = apiError?.isOffline ?? !navigator.onLine

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center gap-3 rounded-md border border-border bg-surface px-6 py-12 text-center',
        className,
      )}
    >
      <span aria-hidden className="text-warning">
        {offline ? (
          <WifiOff className="size-8" strokeWidth={1.75} />
        ) : (
          <AlertTriangle className="size-8" strokeWidth={1.75} />
        )}
      </span>
      <h3 className="text-section">{offline ? t('offline') : t('generic')}</h3>
      <p className="max-w-reading text-body text-text-muted">
        {offline ? t('offlineBody') : t('genericBody')}
      </p>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          {t('common:action.tryAgain', { defaultValue: 'Try again' })}
        </Button>
      )}
      {apiError?.traceId && (
        <p className="font-mono text-meta text-text-muted">
          {t('traceId', { traceId: apiError.traceId })}
        </p>
      )}
    </div>
  )
}
